import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api, BACKEND_URL } from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import VideoPlayer from "../components/VideoPlayer";

const SEV_COLORS = { high: "bg-red-600 text-white", medium: "bg-yellow-600 text-black", low: "bg-neutral-700 text-white" };

export default function Analysis() {
  const { id } = useParams();
  const [a, setA] = useState(null);
  const [polling, setPolling] = useState(true);
  const [shareUrl, setShareUrl] = useState(null);
  const [sharing, setSharing] = useState(false);
  const playerRef = useRef(null);

  const onPlayerReady = useCallback((controls) => { playerRef.current = controls; }, []);
  const jumpToIssue = (ts) => {
    if (typeof ts !== "number") return;
    if (playerRef.current) {
      playerRef.current.jumpTo(ts);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  useEffect(() => {
    let mounted = true;
    const fetch = async () => {
      try {
        const r = await api.get(`/analyses/${id}`);
        if (!mounted) return;
        setA(r.data);
        if (r.data.share_token) {
          setShareUrl(`${window.location.origin}/share/${r.data.share_token}`);
        }
        if (r.data.status === "complete" || r.data.status === "failed") setPolling(false);
      } catch (e) { console.error("analysis fetch failed", e); toast.error("Could not load"); setPolling(false); }
    };
    fetch();
    const t = setInterval(() => { if (polling) fetch(); }, 3500);
    return () => { mounted = false; clearInterval(t); };
  }, [id, polling]);

  const enableShare = async () => {
    setSharing(true);
    try {
      const r = await api.post(`/analyses/${id}/share`);
      const url = `${window.location.origin}/share/${r.data.share_token}`;
      setShareUrl(url);
      try { await navigator.clipboard.writeText(url); toast.success("Share link copied"); }
      catch { toast.success("Share link ready"); }
    } catch { toast.error("Could not create link"); }
    finally { setSharing(false); }
  };

  const revokeShare = async () => {
    try {
      await api.delete(`/analyses/${id}/share`);
      setShareUrl(null);
      toast.success("Link revoked");
    } catch { toast.error("Could not revoke"); }
  };

  if (!a) return <div className="text-neutral-400">Loading…</div>;

  const isBusy = ["queued", "extracting_frames", "analyzing"].includes(a.status);

  return (
    <div>
      <Link to="/dashboard" className="text-neutral-400 hover:text-white text-sm font-display uppercase tracking-widest">← Back to dashboard</Link>
      <div className="rs-underline mt-4 mb-3" />
      <h1 className="font-display-heavy text-4xl">{a.sport.toUpperCase()} · ANALYSIS</h1>
      <div className="flex items-center gap-3 mt-1">
        <p className="text-neutral-400">{new Date(a.created_at).toLocaleString()}</p>
        {a.is_sample && <span data-testid="sample-badge" className="text-xs uppercase font-display font-bold tracking-widest px-2 py-1 rounded bg-neutral-800 text-neutral-300">Demo clip · free</span>}
      </div>

      {isBusy && (
        <div data-testid="analysis-processing" className="mt-10 border border-neutral-800 rounded-md bg-neutral-900 p-10 text-center">
          <div className="mx-auto w-16 h-16 border-4 border-red-600 border-t-transparent rounded-full animate-spin" />
          <div className="font-display-heavy text-2xl mt-6">ANALYZING YOUR CLIP</div>
          <p className="text-neutral-400 mt-2">
            {a.status === "extracting_frames" ? "Extracting frames…" : a.status === "analyzing" ? "AI coach is reviewing…" : "Queued…"}
          </p>
          <p className="text-neutral-500 text-sm mt-4">You can close this page — it will keep running.</p>
        </div>
      )}

      {a.status === "failed" && (
        <div data-testid="analysis-failed" className="mt-8 border border-red-800 rounded-md bg-red-950/40 p-6">
          <div className="font-display-heavy text-xl text-red-400">ANALYSIS FAILED</div>
          <p className="text-neutral-300 mt-2">{a.error || "Something went wrong."}</p>
        </div>
      )}

      {(a.status === "complete" || a.is_sample) && a.video_id && (
        <div className="mt-8">
          <VideoPlayer
            src={`${BACKEND_URL}/api/videos/${a.video_id}/stream?token=${encodeURIComponent(localStorage.getItem("repsnap_token") || "")}`}
            issues={a.result?.issues || []}
            onReady={onPlayerReady}
          />
        </div>
      )}

      {a.status === "complete" && a.result && (
        <div className="mt-8 space-y-6">
          <div className="border border-neutral-800 rounded-md bg-neutral-900 p-6">
            <div className="uppercase text-xs text-neutral-400 font-display tracking-widest">Coach's summary</div>
            <p data-testid="analysis-summary" className="mt-2 text-lg text-neutral-100">{a.result.summary}</p>
          </div>

          {a.result.strengths?.length > 0 && (
            <div className="border border-neutral-800 rounded-md bg-neutral-900 p-6">
              <div className="font-display-heavy text-xl mb-3">WHAT YOU'RE DOING WELL</div>
              <ul className="list-disc pl-6 text-neutral-200 space-y-1">
              {(a.result.strengths || []).map((s) => <li key={`str-${s.slice(0, 20)}`}>{s}</li>)}
              </ul>
            </div>
          )}

          <div>
            <div className="font-display-heavy text-2xl mb-3">FIX LIST</div>
            <div className="grid gap-4">
              {(a.result.issues || []).map((it, i) => (
                <div data-testid={`issue-card-${i}`} key={it.id || i} className="border border-neutral-800 rounded-md bg-neutral-900 p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-3 flex-wrap">
                        <div className="font-display-heavy text-xl">{it.title}</div>
                        {typeof it.timestamp_seconds === "number" && (
                          <button data-testid={`jump-to-${i}`} onClick={() => jumpToIssue(it.timestamp_seconds)}
                            className="text-xs uppercase font-display font-bold tracking-widest px-2 py-1 rounded bg-red-600/20 text-red-400 hover:bg-red-600 hover:text-white transition-colors">
                            @ {it.timestamp_seconds.toFixed(1)}s · Jump to moment
                          </button>
                        )}
                      </div>
                      <p className="text-neutral-300 mt-2">{it.explanation}</p>
                    </div>
                    <span className={`text-xs uppercase font-display font-bold tracking-widest px-2 py-1 rounded ${SEV_COLORS[it.severity] || SEV_COLORS.medium}`}>{it.severity}</span>
                  </div>
                  {it.drill && (
                    <div className="mt-4 border-t border-neutral-800 pt-4">
                      <div className="uppercase text-xs text-red-500 font-display tracking-widest">Drill</div>
                      <div className="font-display-heavy text-lg mt-1">{it.drill.name}</div>
                      <p className="text-neutral-300 mt-1">{it.drill.description}</p>
                      {it.drill.reps && <p className="text-neutral-400 text-sm mt-2">Suggested: {it.drill.reps}</p>}
                    </div>
                  )}
                </div>
              ))}
              {(!a.result.issues || a.result.issues.length === 0) && (
                <div className="border border-neutral-800 rounded-md bg-neutral-900 p-6 text-neutral-400">No issues detected — looking clean.</div>
              )}
            </div>
          </div>

          <div className="flex gap-3 flex-wrap">
            <Link to="/plan"><Button data-testid="view-plan-btn" className="h-12 px-6 bg-red-600 hover:bg-red-700 font-semibold">View my plan</Button></Link>
            <Link to="/upload"><Button variant="outline" className="h-12 px-6 border-neutral-700 text-white hover:bg-neutral-900">Analyze another clip</Button></Link>
            <Link to={`/compare?a=${id}`}><Button data-testid="compare-btn" variant="outline" className="h-12 px-6 border-neutral-700 text-white hover:bg-neutral-900">Compare with another clip</Button></Link>
            {!shareUrl ? (
              <Button data-testid="share-with-coach-btn" onClick={enableShare} disabled={sharing} variant="outline" className="h-12 px-6 border-neutral-700 text-white hover:bg-neutral-900">
                {sharing ? "Creating link…" : "Share with coach"}
              </Button>
            ) : (
              <Button data-testid="revoke-share-btn" onClick={revokeShare} variant="ghost" className="h-12 px-6 text-neutral-400 hover:text-white hover:bg-neutral-800">Revoke link</Button>
            )}
          </div>
          {shareUrl && (
            <div data-testid="share-url-panel" className="border border-neutral-800 rounded-md bg-neutral-900 p-4 flex flex-wrap items-center gap-3">
              <div className="text-xs uppercase text-neutral-400 font-display tracking-widest">Read-only link</div>
              <code className="text-sm text-red-400 break-all flex-1">{shareUrl}</code>
              <Button data-testid="copy-share-url-btn" variant="outline" onClick={() => { navigator.clipboard.writeText(shareUrl); toast.success("Copied"); }} className="h-10 border-neutral-700 text-white hover:bg-neutral-800">Copy</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
